/**
 * The drawing hand. Positions are in output pixels; the sprite keeps a constant on-screen size
 * whatever the camera zoom.
 *
 * The built-in sprite is a vector marker (the prototype's, refined). Custom PNG hands with a pen-tip
 * anchor plug in through `HandSprite`.
 */

import type { Ctx2D } from "./canvas";

export interface HandSprite {
  /** draw with the pen tip at (x, y); `px` = output pixels per 1080p pixel */
  draw(ctx: Ctx2D, x: number, y: number, px: number, lifted: number): void;
}

/** Built-in marker, pointing down-left at the tip like a right-handed pen. */
export const MARKER: HandSprite = {
  draw(ctx, x, y, px, lifted) {
    const ang = (-58 * Math.PI) / 180;
    const ux = Math.cos(ang);
    const uy = Math.sin(ang);
    const qx = -uy;
    const qy = ux;
    // lifting the pen raises the marker a little toward the viewer: shift up-right, grow slightly
    const lift = lifted * 6 * px;
    const s = px * (1 + 0.04 * lifted);
    const ox = x + lift * 0.6;
    const oy = y - lift;
    const quad = (d0: number, d1: number, w0: number, w1: number): [number, number][] => [
      [ox + (ux * d0 + qx * w0) * s, oy + (uy * d0 + qy * w0) * s],
      [ox + (ux * d1 + qx * w1) * s, oy + (uy * d1 + qy * w1) * s],
      [ox + (ux * d1 - qx * w1) * s, oy + (uy * d1 - qy * w1) * s],
      [ox + (ux * d0 - qx * w0) * s, oy + (uy * d0 - qy * w0) * s],
    ];
    const poly = (pts: [number, number][], fill: string, stroke?: string) => {
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
      if (stroke) {
        ctx.lineWidth = 2 * px;
        ctx.strokeStyle = stroke;
        ctx.stroke();
      }
    };
    ctx.save();
    // soft shadow, further away when lifted
    ctx.globalAlpha = 0.16;
    const sh = (10 + lift / px) * px;
    poly(
      quad(30, 250, 17, 17).map(([a, b]) => [a + sh * 1.3, b + sh] as [number, number]),
      "#000",
    );
    ctx.globalAlpha = 1;
    poly(quad(0, 14, 3, 7), "#141414");
    poly(quad(14, 40, 7, 16), "#3c3c3c");
    poly(quad(40, 250, 17, 17), "#f5f5f5", "#464646");
    poly(quad(200, 250, 18, 18), "#141414");
    poly(quad(70, 90, 17, 17), "#141414");
    ctx.restore();
  },
};

/** Built-in whiteboard eraser: a felt block with a handle, scrubbing face at (x, y). */
export const ERASER: HandSprite = {
  draw(ctx, x, y, px) {
    const s = px;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate((-14 * Math.PI) / 180);
    ctx.globalAlpha = 0.16;
    ctx.fillStyle = "#000";
    rounded(ctx, -70 * s + 12 * s, -78 * s + 10 * s, 140 * s, 78 * s, 10 * s);
    ctx.fill();
    ctx.globalAlpha = 1;
    // felt
    ctx.fillStyle = "#4a4f57";
    rounded(ctx, -72 * s, -22 * s, 144 * s, 22 * s, 4 * s);
    ctx.fill();
    // body
    ctx.fillStyle = "#3b6fc2";
    rounded(ctx, -70 * s, -78 * s, 140 * s, 58 * s, 12 * s);
    ctx.fill();
    ctx.fillStyle = "#ffffff55";
    rounded(ctx, -58 * s, -70 * s, 116 * s, 12 * s, 6 * s);
    ctx.fill();
    ctx.restore();
  },
};

function rounded(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** A PNG hand with its pen-tip anchor (sprite pixels), drawn at 1080p size `scale`. */
export function imageHand(img: CanvasImageSource, anchorX: number, anchorY: number, scale = 1): HandSprite {
  return {
    draw(ctx, x, y, px, lifted) {
      const s = scale * px * (1 + 0.04 * lifted);
      const lift = lifted * 6 * px;
      ctx.drawImage(
        img,
        x + lift * 0.6 - anchorX * s,
        y - lift - anchorY * s,
        (img as { width: number }).width * s,
        (img as { height: number }).height * s,
      );
    },
  };
}
